/* ==========================================================================
   Ecoline — cart-drawer.js (Header/Footer/Drawers owner)
   Client-renders cart line items from /cart.js into [data-cart-lines] and
   keeps the drawer's progress band, totals, savings and states in sync.
   Depends on window.Ecoline (assets/app.js): cart, drawer, formatMoney,
   shipProgress — and the `ecoline:cart-updated` document event.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__elCartDrawerInit) return;
  window.__elCartDrawerInit = true;

  var busy = false;
  var mrpCache = {};   /* product handle -> { variantId: compare_at_price (paise) } */
  var mrpPending = {}; /* product handle -> Promise */

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function drawerEl() {
    return document.querySelector('[data-drawer="cart"]');
  }

  function money(paise) {
    return window.Ecoline && window.Ecoline.formatMoney
      ? window.Ecoline.formatMoney(paise)
      : String(paise / 100);
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sizedImage(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* ------------------------------------------------------------------------
     MRP lookup — /cart.js items carry no compare-at price, so fetch
     /products/<handle>.js once per handle and cache variant MRPs.
     ------------------------------------------------------------------------ */
  function fetchMrpMaps(handles) {
    var jobs = handles.map(function (handle) {
      if (mrpCache[handle]) return Promise.resolve(mrpCache[handle]);
      if (!mrpPending[handle]) {
        mrpPending[handle] = fetch('/products/' + handle + '.js', {
          headers: { Accept: 'application/json' }
        })
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (product) {
            var map = {};
            if (product && product.variants) {
              product.variants.forEach(function (variant) {
                map[variant.id] = variant.compare_at_price || 0;
              });
            }
            mrpCache[handle] = map;
            return map;
          })
          .catch(function () {
            mrpCache[handle] = {};
            return mrpCache[handle];
          });
      }
      return mrpPending[handle];
    });
    return Promise.all(jobs).then(function () { return mrpCache; });
  }

  /* ------------------------------------------------------------------------
     Line item markup (image 72px rounded · title + remove ✕ · finish · qty
     stepper · line price) — mirrors the SSR markup in
     snippets/cart-drawer.liquid exactly (incl. .el-cart-line__row-top and
     the [data-line-remove] anchor that snippet's inline script binds to).
     `index` is the 0-based position in cart.items — Shopify's ?line= param
     is 1-based, so the no-JS fallback href uses index + 1.
     ------------------------------------------------------------------------ */
  function lineHtml(item, index) {
    var finish = item.variant_title && item.variant_title !== 'Default Title'
      ? item.variant_title
      : '';
    var media = item.image
      ? '<img class="el-cart-line__img" src="' + escapeHtml(sizedImage(item.image, 144)) + '"' +
        ' alt="' + escapeHtml(item.product_title) + '" width="72" height="72" loading="lazy">'
      : '<span class="el-cart-line__img el-cart-line__img--ph" aria-hidden="true"></span>';

    return (
      '<div class="el-cart-line" data-line-key="' + escapeHtml(item.key) + '">' +
        '<a class="el-cart-line__media" href="' + escapeHtml(item.url) + '" tabindex="-1" aria-hidden="true">' + media + '</a>' +
        '<div class="el-cart-line__info">' +
          '<div class="el-cart-line__row-top">' +
            '<a class="el-cart-line__title" href="' + escapeHtml(item.url) + '">' + escapeHtml(item.product_title) + '</a>' +
            '<a class="el-cart-line__remove" href="/cart/change?line=' + (index + 1) + '&amp;quantity=0" rel="nofollow" data-line-remove' +
              ' aria-label="Remove ' + escapeHtml(item.product_title) + ' from cart">✕</a>' +
          '</div>' +
          (finish ? '<div class="el-cart-line__finish">' + escapeHtml(finish) + '</div>' : '') +
          '<div class="el-cart-line__row">' +
            '<div class="el-cart-line__qty" aria-label="Quantity">' +
              '<button type="button" class="el-cart-line__step" data-qty-change="-1" aria-label="Decrease quantity of ' + escapeHtml(item.product_title) + '">−</button>' +
              '<span class="el-cart-line__count" data-line-qty>' + item.quantity + '</span>' +
              '<button type="button" class="el-cart-line__step" data-qty-change="1" aria-label="Increase quantity of ' + escapeHtml(item.product_title) + '">+</button>' +
            '</div>' +
            '<div class="el-cart-line__price">' + money(item.final_line_price) + '</div>' +
          '</div>' +
        '</div>' +
      '</div>'
    );
  }

  /* ------------------------------------------------------------------------
     Render the whole drawer from a /cart.js payload.
     ------------------------------------------------------------------------ */
  function render(cart) {
    var drawer = drawerEl();
    if (!drawer || !cart) return;

    var isEmpty = !cart.items || cart.item_count === 0;
    var filled = qs('[data-cart-filled]', drawer);
    var empty = qs('[data-cart-empty]', drawer);
    if (filled) filled.hidden = isEmpty;
    if (empty) empty.hidden = !isEmpty;

    var label = qs('[data-cart-count-label]', drawer);
    if (label) {
      label.textContent = '· ' + cart.item_count + (cart.item_count === 1 ? ' item' : ' items');
    }

    if (isEmpty) return;

    var lines = qs('[data-cart-lines]', drawer);
    if (lines) {
      lines.innerHTML = cart.items.map(function (item, index) {
        return lineHtml(item, index);
      }).join('');
    }

    /* Free-shipping progress band */
    var progress = window.Ecoline.shipProgress(cart.total_price);
    var msg = qs('[data-ship-msg]', drawer);
    if (msg) msg.textContent = progress.msg;
    var bar = qs('[data-ship-bar]', drawer);
    if (bar) bar.style.width = progress.pct + '%';

    /* Totals */
    var subtotal = qs('[data-cart-subtotal]', drawer);
    if (subtotal) subtotal.textContent = money(cart.total_price);

    var delivery = qs('[data-cart-delivery]', drawer);
    if (delivery) {
      delivery.textContent = progress.free ? 'Free' : 'Calculated at checkout';
      delivery.classList.toggle('is-free', progress.free);
    }

    var checkoutTotal = qs('[data-checkout-total]', drawer);
    if (checkoutTotal) checkoutTotal.textContent = money(cart.total_price);

    /* Savings row (MRP total vs subtotal, only if savings > 0) */
    var savingsRow = qs('[data-cart-savings]', drawer);
    if (!savingsRow) return;

    var showMrp = !(window.EcolineSettings && window.EcolineSettings.showMrp === false);
    if (!showMrp) {
      savingsRow.hidden = true;
      return;
    }

    var handles = [];
    cart.items.forEach(function (item) {
      if (item.handle && handles.indexOf(item.handle) === -1) handles.push(item.handle);
    });

    var renderToken = cart.token + ':' + cart.item_count + ':' + cart.total_price;
    savingsRow.setAttribute('data-render-token', renderToken);

    fetchMrpMaps(handles).then(function (cache) {
      /* stale response guard — a newer render may have happened */
      if (savingsRow.getAttribute('data-render-token') !== renderToken) return;

      var mrpTotal = 0;
      cart.items.forEach(function (item) {
        var map = cache[item.handle] || {};
        var unitMrp = map[item.variant_id] || 0;
        var unitPrice = typeof item.final_price === 'number' ? item.final_price : item.price;
        mrpTotal += Math.max(unitMrp, unitPrice) * item.quantity;
      });

      var savings = mrpTotal - cart.total_price;
      if (savings > 0) {
        var mrpEl = qs('[data-cart-mrp-total]', drawer);
        var saveEl = qs('[data-cart-save]', drawer);
        if (mrpEl) mrpEl.textContent = 'MRP total ' + money(mrpTotal);
        if (saveEl) saveEl.textContent = 'You save ' + money(savings);
        savingsRow.hidden = false;
      } else {
        savingsRow.hidden = true;
      }
    });
  }

  /* ------------------------------------------------------------------------
     Qty stepper — event delegation on the drawer. −/+ call
     Ecoline.cart.change(lineKey, qty); the resulting `ecoline:cart-updated`
     event re-renders the drawer (and header badges via app.js).
     ------------------------------------------------------------------------ */
  function onClick(event) {
    var btn = event.target.closest ? event.target.closest('[data-qty-change]') : null;
    if (!btn) return;
    var line = btn.closest('[data-line-key]');
    if (!line || busy) return;

    var key = line.getAttribute('data-line-key');
    var countEl = line.querySelector('[data-line-qty]');
    var qty = parseInt(countEl && countEl.textContent, 10) || 0;
    var next = Math.max(0, qty + parseInt(btn.getAttribute('data-qty-change'), 10));

    busy = true;
    line.classList.add('is-busy');
    window.Ecoline.cart.change(key, next)
      .catch(function () {
        /* re-sync from the server if the change failed */
        return window.Ecoline.cart.get().then(render).catch(function () {});
      })
      .then(function () {
        busy = false;
        line.classList.remove('is-busy');
      });
  }

  function init() {
    var drawer = drawerEl();
    if (!drawer || !window.Ecoline) return;

    drawer.addEventListener('click', onClick);

    document.addEventListener('ecoline:cart-updated', function (event) {
      if (event.detail && typeof event.detail.item_count === 'number') {
        render(event.detail);
      }
    });

    /* Initial client render of line items */
    window.Ecoline.cart.get().then(render).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
